import { getVm } from './vm-helper';

import {
	MonetizationType,
	ObjectType,
	Offer as OfferGraphql,
	OfferType,
	Package,
	SportObjectType,
	SportType,
} from '@/@types/graphql-types';
import { btoa } from '@/helpers/base64-helper';
import { toRESTMonetizationType, toRESTPresentationType } from '@/helpers/graphql-model-helper';
import {
	SnowplowClickoutContext,
	SnowplowClickoutContextAffiliateLink,
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BuyboxOfferCategory, BuyboxOfferGraphql, BuyboxOffers } from '@/interfaces/buybox';
import Provider from '@/interfaces/provider';
import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';
import { Offer, OfferMonetizationType, OfferPresentationType, TitleObjectType } from '@/interfaces/titles';

const CATEGORY_MONETIZATIONS: Record<BuyboxOfferCategory, OfferMonetizationType[]> = {
	[BuyboxOfferCategory.STREAM]: [
		OfferMonetizationType.FLATRATE,
		OfferMonetizationType.FREE,
		OfferMonetizationType.ADS,
		OfferMonetizationType.FLATRATE_AND_BUY,
	],
	[BuyboxOfferCategory.RENT]: [OfferMonetizationType.RENT],
	[BuyboxOfferCategory.BUY]: [OfferMonetizationType.BUY],
	[BuyboxOfferCategory.PHYSICAL]: [OfferMonetizationType.BUY],
	[BuyboxOfferCategory.BUNDLE]: [OfferMonetizationType.BUNDLE],
	[BuyboxOfferCategory.CINEMA]: [OfferMonetizationType.CINEMA],
};

const CATEGORY_MONETIZATIONS_GRAPHQL: Record<BuyboxOfferCategory, MonetizationType[] | 'BUNDLE'> = {
	[BuyboxOfferCategory.STREAM]: [
		MonetizationType.Flatrate,
		MonetizationType.Free,
		MonetizationType.Ads,
		MonetizationType.FlatrateAndBuy,
	],
	[BuyboxOfferCategory.RENT]: [MonetizationType.Rent],
	[BuyboxOfferCategory.BUY]: [MonetizationType.Buy],
	[BuyboxOfferCategory.PHYSICAL]: [MonetizationType.Buy],
	[BuyboxOfferCategory.CINEMA]: 'BUNDLE',
	// We need add BUNDLE as Monetization Type (exception) to be able to display bundles in Promoted BuyBox
	[BuyboxOfferCategory.BUNDLE]: 'BUNDLE',
};

const CATEGORY_PRESENTATION_TYPES = {
	[BuyboxOfferCategory.STREAM]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.RENT]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.BUY]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.PHYSICAL]: [OfferPresentationType.BLURAY, OfferPresentationType.DVD],
	[BuyboxOfferCategory.BUNDLE]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.CINEMA]: [OfferPresentationType.HD],
};

const MONETIZATION_LABEL_TRANSLATABLES = {
	[OfferMonetizationType.FLATRATE]: 'WEBAPP_FLAT',
	[OfferMonetizationType.FLATRATE_AND_BUY]: 'WEBAPP_FLAT',
	[OfferMonetizationType.ADS]: 'WEBAPP_ADS',
	[OfferMonetizationType.FREE]: 'WEBAPP_FREE',
	[OfferMonetizationType.BUNDLE]: 'WEBAPP_FLAT',
};

const PRESENTATION_TYPE_PRIORITY = [OfferPresentationType._4K, OfferPresentationType.HD, OfferPresentationType.SD];

export type NarrativeObjectType =
	| ObjectType.Show
	| ObjectType.ShowEpisode
	| ObjectType.ShowSeason
	| ObjectType.Movie
	| ObjectType.Person;

export const enum OfferLocation {
	REGULAR_BUYBOX = 0,
	PROMOTED_BUYBOX = 1,
	BACKDROP = 2,
	MIDDLE_BUYBOX = 3,
}

// These strings are checked as is in JW-UCT becareful if changing them
export const BuyboxParam = {
	Normal: 'normal',
	Promoted: 'special',
	Sport: 'sports',
};

export type BuyboxOfferType = (typeof BuyboxParam)[keyof typeof BuyboxParam];

export const OfferHelper = {
	getBuyboxOffers(
		offers: Offer[],
		buyboxCategories: BuyboxOfferCategory[],
		language: string, // used for price formatting
		country: string, // used for price formatting
		objectType: TitleObjectType,
		providersById: Record<string, Provider>,
		selectedPresentationType?: OfferPresentationType,
		domainSessionId?: string,
		promoted = BuyboxParam.Normal,
		allowedProviderIds: number[] = [] // filter by specific providers
	): BuyboxOffers {
		// FILTER and SORTING
		return buyboxCategories.reduce((root, buyboxCategory) => {
			root[buyboxCategory] =
				offers &&
				offers
					// filter monetization types
					.filter(offer => CATEGORY_MONETIZATIONS[buyboxCategory].includes(offer.monetization_type))
					// filter presentation types (for physical mainly)
					.filter(offer => {
						return (CATEGORY_PRESENTATION_TYPES[buyboxCategory] || []).includes(offer.presentation_type);
					})
					// filter for selectedPresentationType if they're not physical category
					.filter(offer => {
						// ignore selectedPresentationType for physical category
						if (![BuyboxOfferCategory.PHYSICAL].includes(buyboxCategory)) {
							if (<string>selectedPresentationType === OfferMonetizationType.FREE) {
								return [OfferMonetizationType.FREE, OfferMonetizationType.ADS].includes(
									offer.monetization_type
								);
							}

							// for non-4k flatrate offers, we want to show hd and sd anyway (because some can only be scraped in hd, but not sd and vice versa).
							// only when 4k presentation type is selected, it should filter specifically for it
							return (
								!selectedPresentationType ||
								offer.presentation_type === selectedPresentationType ||
								(offer.monetization_type === OfferMonetizationType.FLATRATE &&
									offer.presentation_type !== OfferPresentationType._4K &&
									selectedPresentationType !== OfferPresentationType._4K)
							);
						}
						return true;
					})
					.filter(offer => offer.provider_id in providersById)
					.filter(
						offer => allowedProviderIds.length === 0 || allowedProviderIds.indexOf(offer.provider_id) !== -1
					)
					// SORTING by lowest price and highest provider priority
					.sort((a, b) => {
						// prettier-ignore
						return [
							{
								// amount of seasons / episodes
								score: 0 - ((a.element_count || 0) - (b.element_count || 0)),
								weight: 10000,
							},
							{
								// display priority for seasons
								score: ((a.type === 'aggregated') ? providersById[a.provider_id].display_priority : 0) - ((b.type === 'aggregated') ? providersById[b.provider_id].display_priority : 0),
								weight: 100,
							},
							{
								// best price
								score: ((a.type !== 'aggregated') ? (a.retail_price || 0) : 0) - ((b.type !== 'aggregated') ? (b.retail_price || 0) : 0),
								weight: 100,
							},
							{
								// selectedPresentationType
								score: (a.presentation_type === selectedPresentationType ? 0 : 1) - (b.presentation_type === selectedPresentationType ? 0 : 1),
								weight: 10,
							},
							{
								// base display priority
								score: providersById[a.provider_id].display_priority - providersById[b.provider_id].display_priority,
								weight: 1,
							},
							{
								// base presentation type
								score: PRESENTATION_TYPE_PRIORITY.indexOf(a.presentation_type) - PRESENTATION_TYPE_PRIORITY.indexOf(b.presentation_type),
								weight: 0.1,
							},
						].reduce((root, item) => root + (item.score * item.weight), 0);
					})
					// don't allow duplicates (same provider_id + category) except for physical category
					.filter((offer, index, offers) => {
						return (
							[BuyboxOfferCategory.PHYSICAL].indexOf(buyboxCategory) !== -1 ||
							offers.findIndex(o => o.provider_id === offer.provider_id) === index
						);
					})
					// TRANSFORMING into BuyboxOffer format
					.map(offer => {
						const url = getUctLink(
							offer,
							providersById[offer.provider_id],
							country,
							promoted,
							domainSessionId
						);
						const item = {
							provider: providersById[offer.provider_id],
							offer, // full original offer
							price: getPriceForOffer(offer, `${language}-${country}`),
							translatable: getTranslatableForOffer(offer, objectType),
							presentationType: offer.presentation_type,
							url, // @TODO: Get UCT URL!
						};
						return item;
					});
			return root;
		}, {} as BuyboxOffers);
	},

	getBuyboxOffersGraphql(
		offers: OfferGraphql[],
		country: string, // used for price formatting
		objectType: ObjectType,
		providersById: Record<string, Provider>,
		promoted = BuyboxParam.Normal
	): BuyboxOfferGraphql[] {
		return offers.map(offer => {
			const url = getUctLinkGraphql(offer, country, promoted) || '';

			return {
				provider: providersById[offer.package.packageId],
				offer, // full original offer
				price: offer?.retailPrice || '',
				translatable: getTranslatableForOfferGraphql(offer, objectType),
				presentationType: offer.presentationType,
				url,
			} as BuyboxOfferGraphql;
		});
	},

	getBuyboxCategoryByMonetization(monetizationType: OfferMonetizationType): BuyboxOfferCategory | undefined {
		return (Object.keys(CATEGORY_MONETIZATIONS) as (keyof typeof CATEGORY_MONETIZATIONS)[]).find(
			key => CATEGORY_MONETIZATIONS[key].indexOf(monetizationType) !== -1
		);
	},

	getBuyboxCategoryByMonetizationGraphql(monetizationType: MonetizationType): BuyboxOfferCategory | undefined {
		return (Object.keys(CATEGORY_MONETIZATIONS_GRAPHQL) as (keyof typeof CATEGORY_MONETIZATIONS)[]).find(
			key => CATEGORY_MONETIZATIONS_GRAPHQL[key].indexOf(monetizationType) !== -1
		);
	},

	trackClickOut(
		justwatchId: string,
		offer: Offer,
		provider: Provider,
		objectType: TitleObjectType,
		objectId: number,
		seasonNumber?: number | null,
		episodeNumber?: number | null,
		offerLocation?: OfferLocation,
		additionalContexts?: SnowplowContext[],
		sportObjectType?: SportObjectType
	) {
		// A bit hacky but for clickouts, we do a double tracking.
		// We track in Snowplow (slow) and directly in the consumer api (real time).
		const contexts: SnowplowContext[] = [
			SnowplowClickoutContext.fromProviderOffer(offer, provider, sportObjectType),
			new SnowplowTitleContext(objectId, objectType, seasonNumber, episodeNumber),
			...(additionalContexts ? additionalContexts : []),
		];

		TrackingHelper.trackEvent(
			'clickout',
			{
				action: offer.monetization_type,
				label: provider.clear_name,
				property: offer.presentation_type,
				value: offerLocation ?? OfferLocation.REGULAR_BUYBOX,
			},
			contexts
		);
	},

	trackClickOutGraphql(
		justwatchId: string,
		offer: Pick<
			OfferGraphql,
			'monetizationType' | 'presentationType' | 'retailPriceValue' | 'currency' | 'lastChangeRetailPriceValue'
		> & { package: Pick<Package, 'packageId' | 'clearName'> },
		objectType: ObjectType,
		objectId: number,
		seasonNumber?: number | null,
		episodeNumber?: number | null,
		offerLocation?: OfferLocation,
		additionalContexts?: SnowplowContext[],
		contentType?: SportObjectType | ClickoutContentType.LeavingSoon
	) {
		// A bit hacky but for clickouts, we do a double tracking.
		// We track in Snowplow (slow) and directly in the consumer api (real time).
		const contexts: SnowplowContext[] = [
			SnowplowClickoutContextGraphql.fromProviderOffer(offer, contentType),
			new SnowplowTitleContextGraphql(objectId, objectType, seasonNumber, episodeNumber),
			...(additionalContexts ? additionalContexts : []),
		];

		TrackingHelper.trackEvent(
			'clickout',
			{
				action: toRESTMonetizationType(offer.monetizationType),
				label: offer.package.clearName,
				property: toRESTPresentationType(offer.presentationType),
				value: offerLocation ?? OfferLocation.REGULAR_BUYBOX,
			},
			contexts
		);
	},

	trackClickOutSports(
		offer: Pick<
			OfferGraphql,
			'monetizationType' | 'presentationType' | 'retailPriceValue' | 'currency' | 'lastChangeRetailPriceValue'
		> & { package: Pick<Package, 'packageId' | 'clearName' | 'technicalName'> },
		modulePositionInList: number | undefined,
		matchPositionInList: number | undefined,
		sportObjectType: SportObjectType,
		offerLocation: string,
		additionalContexts?: SnowplowContext[]
	) {
		// A bit hacky but for clickouts, we do a double tracking.
		// We track in Snowplow (slow) and directly in the consumer api (real time).
		const contexts: SnowplowContext[] = [
			// clickout context
			SnowplowClickoutContextGraphql.fromProviderOffer(offer, sportObjectType),
			...(additionalContexts ? additionalContexts : []),
			// package context
			TrackingHelper.getPackageContext(offer.package.packageId, offer.package.technicalName),
		];

		TrackingHelper.trackEvent(
			'clickout',
			{
				action: 'sports',
				label: offerLocation,
				property:
					modulePositionInList !== undefined && matchPositionInList !== undefined
						? `${modulePositionInList}_${matchPositionInList}`
						: undefined, // <{position of module in list}__{position of match in list}>
			},
			contexts
		);

		if (offerLocation === 'watchnow') {
			// legacy reasons we need to additionally track watchnow events
			TrackingHelper.trackEvent(
				'userinteraction',
				{
					action: 'watchnow_clicked',
					label: 'sports',
				},
				contexts
			);
		}
	},

	hasStreamingOffers(offers: Offer[]): boolean {
		return offers.length > 0;
	},

	hasStreamingOffersGraphql(offers: OfferGraphql[]): boolean {
		return offers.length > 0;
	},

	hasFlatrateOffers(offers: Offer[]): boolean {
		return offers.some((offer: Offer) => offer.monetization_type === OfferMonetizationType.FLATRATE);
	},

	hasFlatrateOffersGraphql(offers: OfferGraphql[]): boolean {
		return offers.some((offer: OfferGraphql) => offer.monetizationType === MonetizationType.Flatrate);
	},
};

function getPriceForOffer(offer: Offer, locale: string): string {
	if (offer.retail_price !== undefined) {
		return offer.retail_price
			.toLocaleString(locale, {
				style: 'currency',
				currency: offer.currency,
			})
			.replace(/\s+/, ''); // The space is actually a non-breaking-space D:
	}
	return '';
}

/**
 * translatable right unter the provider icon, e.g. STREAM or RENT or TICKET.
 */
function getTranslatableForOffer(
	offer: Offer,
	objectType: TitleObjectType
): false | { name: string; properties?: any } {
	if (offer.element_count && (offer.type === 'aggregated' || (offer.type !== 'aggregated' && !offer.retail_price))) {
		if (offer.element_count > 1) {
			if (objectType === TitleObjectType.SHOW) {
				return { name: 'WEBAPP_X_SEASONS', properties: { seasons: offer.element_count } };
			} else if (objectType === TitleObjectType.SEASON) {
				return { name: 'WEBAPP_X_EPISODES', properties: { episodes: offer.element_count } };
			}
		} else if (offer.element_count === 1) {
			if (objectType === TitleObjectType.SHOW) {
				return { name: 'WEBAPP_ONE_SEASON' };
			} else if (objectType === TitleObjectType.SEASON) {
				return { name: 'WEBAPP_ONE_EPISODE' };
			}
		}
	}
	if (offer.monetization_type in MONETIZATION_LABEL_TRANSLATABLES) {
		return { name: (MONETIZATION_LABEL_TRANSLATABLES as any)[offer.monetization_type] };
	}
	return false;
}

export function getTranslatableForOfferGraphql(
	offer: OfferGraphql,
	objectType: ObjectType
): false | { name: string; properties?: any } {
	const vm = getVm();

	if (offer.elementCount && (offer.type === OfferType.Aggregated || !offer.retailPriceValue)) {
		if (offer.elementCount > 1) {
			if (objectType === ObjectType.Show) {
				return vm.$t('WEBAPP_X_SEASONS', { seasons: offer.elementCount });
			} else if (objectType === ObjectType.ShowSeason) {
				return vm.$t('WEBAPP_X_EPISODES', { episodes: offer.elementCount });
			}
		} else if (offer.elementCount === 1) {
			if (objectType === ObjectType.Show) {
				return vm.$t('WEBAPP_ONE_SEASON');
			} else if (objectType === ObjectType.ShowSeason) {
				return vm.$t('WEBAPP_ONE_EPISODE');
			}
		}
	}

	return (
		{
			[MonetizationType.Flatrate]: vm.$t('WEBAPP_FLAT'),
			[MonetizationType.FlatrateAndBuy]: vm.$t('WEBAPP_FLAT'),
			[MonetizationType.Ads]: vm.$t('WEBAPP_ADS'),
			[MonetizationType.Free]: vm.$t('WEBAPP_FREE'),
		}[offer.monetizationType as string] ?? false
	);
}

function getUctLink(
	offer: Offer,
	provider: Provider,
	country: string,
	buyboxParam: BuyboxOfferType,
	domainSessionId?: string
) {
	const clickoutUrl = encodeURIComponent(offer.urls.standard_web);

	const baseContexts = TrackingHelper.getAdditionalContexts();
	if (offer.monetization_type !== OfferMonetizationType.BUNDLE) {
		baseContexts.push(SnowplowClickoutContext.fromProviderOffer(offer, provider));
	}

	const base64EncodedContexts = encodeContexts(baseContexts);
	// if domainSessionId is available attach it to the clickout url, otherwise just omit it
	return `${JW_CONFIG.CLICK_URL}/a?r=${clickoutUrl}&cx=${base64EncodedContexts}&uct_country=${country}&uct_buybox=${buyboxParam}&sid=${domainSessionId}`;
}

export type UctLinkOffer = Pick<
	OfferGraphql,
	| 'monetizationType'
	| 'standardWebURL'
	| 'presentationType'
	| 'retailPriceValue'
	| 'currency'
	| 'lastChangeRetailPriceValue'
> & { package: Pick<Package, 'packageId' | 'clearName'> };

export function getUctLinkGraphql(
	offer: UctLinkOffer,
	country: string,
	buyboxParam: BuyboxOfferType,
	domainSessionId?: string,
	sportType?: SportType,
	conversionTracking?: string | null
) {
	const clickoutUrl = encodeURIComponent(offer.standardWebURL || '');
	const baseContexts = TrackingHelper.getAdditionalContexts();
	const clickoutContext = SnowplowClickoutContextAffiliateLink.fromProviderOffer(offer);
	const sport = sportType?.toLowerCase() || '';
	const ct = conversionTracking || '';

	const base64EncodedContexts = encodeContexts(baseContexts.concat(clickoutContext));
	// if domainSessionId is available attach it to the clickout url, otherwise just omit it
	return `${JW_CONFIG.CLICK_URL}/a?r=${clickoutUrl}&cx=${base64EncodedContexts}&uct_country=${country}&uct_buybox=${buyboxParam}&uct_sport=${sport}&sid=${domainSessionId}&uct_ct=${ct}`;
}

export function encodeContexts(contexts: SnowplowContext[]): string {
	const completeContext = {
		schema: 'iglu:com.snowplowanalytics.snowplow/contexts/jsonschema/1-0-0',
		data: contexts.map(c => c.toObject()),
	};

	let base64EncodedContext;
	try {
		const contextString = JSON.stringify(completeContext);
		base64EncodedContext = base64EncodeUrl(btoa(unescape(encodeURIComponent(contextString))));
	} catch (error) {
		console.error('[OfferHelper] encodeContexts: Failed to generate encoded context string: ', error);
		base64EncodedContext = '';
	}
	return base64EncodedContext;
}

/**
 * use this to make a Base64 encoded string URL friendly,
 * i.e. '+' and '/' are replaced with '-' and '_' also any trailing '='
 * characters are removed
 * source: https://jsfiddle.net/magikMaker/7bjaT
 * @param {String} str the encoded string
 * @returns {String} the URL friendly encoded String
 */
function base64EncodeUrl(str: string) {
	return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/\=+$/, '');
}
